import { FunctionComponent, ReactNode, useRef, useState } from "react";
import { Button } from "@chakra-ui/react";
import { saveAs } from "file-saver";
import { Font, pdf } from "@react-pdf/renderer";
import { useRecoilValue } from "recoil";
import {
  flowRateBoundariesSelector,
  groundObstaclesState,
  maxNumberOfGroundObstaclesSelecter,
  maxNumberOfRoofObstaclesSelecter,
  monitorHeightMaxValueSelector,
  monitorHeightMinValueSelector,
  outputSelector,
  pressureMaxValueSelector,
  pressureMinValueSelector,
  printLocalAtom,
  roofObstaclesState,
  selectedAngleState,
  selectedFlowRateState,
  selectedMediumState,
  selectedMonitorHeightState,
  selectedMonitorImageSelector,
  selectedMonitorNozzleImageSelector,
  selectedMonitorSelector,
  selectedNozzleSelector,
  selectedPressureState,
  unitsState,
} from "../../state/state";
import AngleGui from "../angleGui";
import html2canvas from "html2canvas";
import Chart from "../chart";
// @ts-ignore
import proximaNova from "../../assets/pdf/ProximaNova.ttf";
// @ts-ignore
import proximaNovaBold from "../../assets/pdf/ProximaNovaBold.ttf";
import {
  angleGuiHeight,
  angleGuiWidth,
  chartHeight,
  chartWidth,
} from "./pdfDocStyles";
import PdfDoc from "./pdfDoc";
import { useTranslation } from "react-i18next";
import { AvailableLanguages } from "../../language/i18n";

Font.register({
  family: "ProximaNova",
  fonts: [
    {
      src: proximaNova,
    },
    {
      src: proximaNovaBold,
      fontWeight: "bold",
    },
  ],
});

type PdfDownloadButtonProps = {
  children?: ReactNode;
};

const PdfDownloadButton: FunctionComponent<PdfDownloadButtonProps> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const unit = useRecoilValue(unitsState);
  const printLocal = useRecoilValue(printLocalAtom);
  const currentLanguage = i18n.language as AvailableLanguages;

  const selectedMonitorImage = useRecoilValue(selectedMonitorImageSelector);
  const selectedMonitorNozzleImage = useRecoilValue(
    selectedMonitorNozzleImageSelector
  );
  const selectedMonitor = useRecoilValue(selectedMonitorSelector);
  const selectedNozzle = useRecoilValue(selectedNozzleSelector);
  const selectedAngle = useRecoilValue(selectedAngleState);

  const flowRateBoundaries = useRecoilValue(flowRateBoundariesSelector);
  const flowRateMinValue = flowRateBoundaries?.flowRateMinimum ?? 0;
  const flowRateMaxValue = flowRateBoundaries?.flowRateMaximum ?? 0;
  const selectedFlowRate = useRecoilValue(selectedFlowRateState);

  const pressureMinValue = useRecoilValue(pressureMinValueSelector);
  const pressureMaxValue = useRecoilValue(pressureMaxValueSelector);
  const selectedPressure = useRecoilValue(selectedPressureState);

  const monitorHeightMinValue = useRecoilValue(monitorHeightMinValueSelector);
  const monitorHeightMaxValue = useRecoilValue(monitorHeightMaxValueSelector);
  const selectedMonitorHeight = useRecoilValue(selectedMonitorHeightState);

  const selectedMedium = useRecoilValue(selectedMediumState);

  const maxNumberOfRoofObstacles = useRecoilValue(
    maxNumberOfRoofObstaclesSelecter
  );

  const maxNumberOfGroundObstacles = useRecoilValue(
    maxNumberOfGroundObstaclesSelecter
  );

  const roofObstacles = useRecoilValue(roofObstaclesState);
  const groundObstacles = useRecoilValue(groundObstaclesState);

  const output = useRecoilValue(outputSelector);

  const [isLoading, setIsLoading] = useState(false);

  const angleGuiRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const generatePdf = async () => {
    // If offline, simply run print dialog from browser.
    if (printLocal) {
      return window.print();
    }

    if (!angleGuiRef.current || !chartRef.current || !output) {
      return;
    }

    const angleGuiCanvas = await html2canvas(angleGuiRef.current, {
      width: angleGuiWidth,
      height: angleGuiHeight,
      useCORS: true,
    });
    const angleGuiDataUrl = angleGuiCanvas.toDataURL();

    const chartCanvas = await html2canvas(chartRef.current, {
      width: chartWidth * 3,
      height: chartHeight * 3,
    });
    const chartDataUrl = chartCanvas.toDataURL();

    const blob = await pdf(
      <PdfDoc
        unit={unit}
        monitorName={selectedMonitor?.name[currentLanguage] ?? ""}
        monitorDescription={
          selectedMonitor?.description[currentLanguage][unit] ?? ""
        }
        nozzleName={selectedNozzle?.name[currentLanguage] ?? ""}
        nozzleDescription={
          selectedNozzle?.description[currentLanguage][unit] ?? ""
        }
        angleGuiDataUrl={angleGuiDataUrl}
        chartDataUrl={chartDataUrl}
        selectedAngle={selectedAngle}
        flowRateMinValue={flowRateMinValue}
        flowRateMaxValue={flowRateMaxValue}
        selectedFlowRate={selectedFlowRate}
        pressureMinValue={pressureMinValue}
        pressureMaxValue={pressureMaxValue}
        selectedPressure={selectedPressure}
        monitorHeightMinValue={monitorHeightMinValue}
        monitorHeightMaxValue={monitorHeightMaxValue}
        selectedMonitorHeight={selectedMonitorHeight}
        roofObstacles={roofObstacles}
        groundObstacles={groundObstacles}
        maxNumberOfRoofObstacles={maxNumberOfRoofObstacles}
        maxNumberOfGroundObstacles={maxNumberOfGroundObstacles}
        output={output}
        selectedMedium={selectedMedium}
      />
    ).toBlob();

    return saveAs(blob, "firedos-export.pdf");
  };

  return (
    <>
      <div
        ref={angleGuiRef}
        style={{
          width: angleGuiWidth,
          height: angleGuiHeight,
          position: "fixed",
          overflow: "visible",
          top: 0,
          left: -5000,
          padding: 50,
          clipPath: "inset(0 100% 0 0)",
        }}
      >
        {selectedMonitorImage && selectedMonitorNozzleImage && (
          <AngleGui
            monitorImage={selectedMonitorImage}
            nozzleImage={selectedMonitorNozzleImage}
            displayMonitorImageAboveNozzle={
              selectedMonitor?.displayImageAboveNozzle ?? false
            }
          />
        )}
      </div>

      <div
        ref={chartRef}
        style={{
          width: chartWidth * 3,
          height: chartHeight * 3,
          position: "fixed",
          top: 0,
          left: -5000,
          clipPath: "inset(0 100% 0 0)",
        }}
      >
        <Chart printMode />
      </div>

      <Button
        colorScheme="brand.red"
        disabled={isLoading}
        isLoading={isLoading}
        onClick={() => {
          setIsLoading(true);
          // Ugly, but neccessary to ensure loading-spinner for safari browser works.
          setTimeout(
            () => generatePdf().finally(() => setIsLoading(false)),
            100
          );
        }}
      >
        {children}
      </Button>
    </>
  );
};

export default PdfDownloadButton;
